import { supabase } from "@/integrations/supabase/client";
import type { ContentType } from "@/types/content";

const DOCUMENT_TYPES: ContentType[] = ["pdf", "doc", "web", "text"];
const MEDIA_TYPES: ContentType[] = ["youtube", "audio", "video", "recording"];

/**
 * Fetches the source text for content from document_text or media_transcripts tables.
 * Used for AI context grounding in flashcards, quizzes, summaries, and chat.
 * 
 * @param contentId - The ID of the content
 * @param contentType - The type of content (pdf, doc, youtube, etc.)
 * @returns The concatenated text content, limited to ~50k characters, or null if not available
 */
export async function getContentSourceText(
  contentId: string,
  contentType: ContentType
): Promise<string | null> {
  if (DOCUMENT_TYPES.includes(contentType)) {
    // Fetch from document_text table
    const { data, error } = await supabase
      .from("document_text")
      .select("page_number, text")
      .eq("content_id", contentId)
      .order("page_number", { ascending: true });

    if (error) {
      console.error("Error fetching document text:", error);
      return null;
    }

    if (!data?.length) return null;

    // Concatenate all pages, limit to ~50k characters for token limits
    const fullText = data.map((p) => p.text).join("\n\n");
    return fullText.slice(0, 150000);
  }

  if (MEDIA_TYPES.includes(contentType)) {
    // Fetch from media_transcripts table
    const { data, error } = await supabase
      .from("media_transcripts")
      .select("start_seconds, text")
      .eq("content_id", contentId)
      .order("start_seconds", { ascending: true });

    if (error) {
      console.error("Error fetching media transcripts:", error);
      return null;
    }

    if (!data?.length) return null;

    // Concatenate all segments, limit to ~50k characters for token limits
    const fullText = data.map((s) => s.text).join(" ");
    return fullText.slice(0, 150000);
  }

  return null;
}

/**
 * Check if content type is a document (has document_text)
 */
export function isDocumentType(contentType: ContentType): boolean {
  return DOCUMENT_TYPES.includes(contentType);
}

/**
 * Check if content type is media (has media_transcripts)
 */
export function isMediaType(contentType: ContentType): boolean {
  return MEDIA_TYPES.includes(contentType);
}
